import { createBaseApi } from '../baseApi';
import { API_ENDPOINTS } from '../../../utils/config';

const shareBundleApi = createBaseApi('shareBundleApi');

/**
 * Share bundles — one link format for a multi-select of clothes, a set of
 * try-on looks, a whole closet, or a whole lookbook.
 *
 * The by-token and claim endpoints also resolve legacy single-result tokens,
 * so links minted by the old ShareModal keep working.
 */
export const enhancedShareBundleApi = shareBundleApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create a share link for a selection / closet / lookbook
    createShareBundle: builder.mutation({
      query: (body) => ({
        url: API_ENDPOINTS.SHARE_BUNDLE.BASE,
        method: 'POST',
        body
      }),
      transformResponse: (response) => {
        if (response?.success) {
          return {
            shareToken: response.shareToken,
            shareUrl: response.shareUrl,
            title: response.title,
            counts: response.counts || { clothes: 0, looks: 0 }
          };
        }
        throw new Error(response?.message || 'Failed to create share link');
      }
    }),

    // Public preview — works logged out, drives the login prompt
    getShareBundleByToken: builder.query({
      query: (token) => ({
        url: API_ENDPOINTS.SHARE_BUNDLE.BY_TOKEN(token),
        method: 'GET'
      }),
      transformResponse: (response) => {
        if (response?.success) {
          return {
            shareToken: response.shareToken,
            sourceType: response.sourceType,
            title: response.title,
            sharer: response.sharer,
            // Present only for sourceType 'profile'
            profile: response.profile || null,
            clothingItems: response.clothingItems || [],
            looks: response.looks || [],
            counts: response.counts || { clothes: 0, looks: 0 }
          };
        }
        return null;
      }
    }),

    // Claim everything in the bundle into the caller's closet
    claimShareBundle: builder.mutation({
      query: (token) => ({
        url: API_ENDPOINTS.SHARE_BUNDLE.CLAIM(token),
        method: 'POST'
      }),
      invalidatesTags: ['ClosetClothes', 'Closets', 'SharedCloset'],
      transformResponse: (response) => {
        if (response?.success) {
          return {
            sharer: response.sharer,
            title: response.title,
            sourceType: response.sourceType,
            closet: response.closet || null,
            claimedItems: response.claimedItems || []
          };
        }
        throw new Error(response?.message || 'Failed to claim items');
      }
    }),

    // Share links the current user has created
    getMyShareBundles: builder.query({
      query: () => ({
        url: API_ENDPOINTS.SHARE_BUNDLE.MINE,
        method: 'GET'
      }),
      providesTags: ['ShareBundle']
    }),

    revokeShareBundle: builder.mutation({
      query: (id) => ({
        url: API_ENDPOINTS.SHARE_BUNDLE.REVOKE(id),
        method: 'PUT'
      }),
      invalidatesTags: ['ShareBundle']
    })
  })
});

export const {
  useCreateShareBundleMutation,
  useGetShareBundleByTokenQuery,
  useLazyGetShareBundleByTokenQuery,
  useClaimShareBundleMutation,
  useGetMyShareBundlesQuery,
  useRevokeShareBundleMutation
} = enhancedShareBundleApi;

export default enhancedShareBundleApi;
